<template>
  <Modal
    :value="visible"
    class-name="ai-drawer-modal"
    width="520"
    :mask-closable="false"
    :closable="true"
    title="AI 助手"
    @on-visible-change="onVisibleChange"
    @on-cancel="closeDrawer">
    <div class="ai-drawer-body">
      <!-- 块 1 任务 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">任务</p>
        <RadioGroup v-model="taskType" @on-change="onTaskTypeChange">
          <Radio v-for="opt in taskOptions" :key="opt.value" :label="opt.value">{{ opt.label }}</Radio>
        </RadioGroup>
        <p v-if="taskType === 'ADVISOR_RENEWAL'" class="ai-hint">仅内部沟通，不向家长发送，不催费</p>
      </Card>

      <!-- 块 2 事实 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">事实</p>
        <p v-if="preparing" class="ai-muted">正在准备事实…</p>
        <p v-else-if="!uid" class="ai-error">{{ missingIdText }}</p>
        <p v-else-if="prepareFailed" class="ai-error">{{ displayError }}</p>
        <div v-else-if="factRows.length">
          <div v-for="row in factRows" :key="row.key" class="ai-fact-row">
            <Checkbox :value="isFactIncluded(row.key)" @on-change="onFactCheck(row.key, $event)">
              <span class="ai-fact-key">{{ row.key }}</span>
            </Checkbox>
            <span class="ai-fact-value">{{ row.display }}</span>
            <span v-if="row.table" class="ai-fact-source">来源：{{ row.table }}</span>
          </div>
        </div>
        <p v-else class="ai-muted">暂无事实</p>
      </Card>

      <!-- 块 3 结果 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">结果</p>
        <div class="ai-result-toolbar">
          <Button type="primary" size="small" :loading="generating" :disabled="!canGenerate" @click="onGenerate">生成</Button>
          <span v-if="isMarket" class="ai-hint">市场角色仅生成，不写回。</span>
        </div>
        <p v-if="!uid" class="ai-error">{{ missingIdText }}</p>
        <p v-else-if="resultError" class="ai-error">{{ displayError }}</p>
        <div v-else-if="output" class="ai-result">
          <div v-if="taskType === 'SALES_FOLLOWUP'">
            <p><b>摘要</b>：{{ output.summary }}</p>
            <p><b>需求</b>：{{ formatList(output.needs) }}</p>
            <p><b>已知事实</b>：{{ formatValue(output.knownFacts) }}</p>
            <p><b>待确认</b>：{{ formatList(output.missingQuestions) }}</p>
            <p><b>意向</b>：{{ output.intentLevel }}</p>
            <p><b>下一步</b>：{{ formatValue(output.nextAction) }}</p>
            <p><b>回复草稿</b>：{{ output.replyDraft }}</p>
            <p><b>提醒</b>：{{ formatList(output.warnings) }}</p>
            <p><b>跟进文案</b>：{{ output.followUpText }}</p>
          </div>
          <div v-else-if="taskType === 'SALES_CALL_ANALYSIS'">
            <p><b>意向</b>：{{ output.intentLevel }} {{ output.intentReason }}</p>
            <p><b>成交</b>：{{ dealLabel(output.dealLikelihood) }} {{ output.dealReason }}</p>
            <p><b>销售质量</b>：{{ output.qualityScore }} {{ formatList(output.qualityReasons) }}</p>
            <p><b>结论</b>：{{ output.followUpText }}</p>
          </div>
          <div v-else-if="taskType === 'ADVISOR_AFTER_CLASS'">
            <p><b>家长反馈</b>：{{ output.parentFeedback }}</p>
          </div>
          <div v-else-if="taskType === 'ADVISOR_WEEKLY'">
            <p><b>总览</b>：{{ output.overview }}</p>
            <p><b>亮点</b>：{{ formatList(output.highlights) }}</p>
            <p><b>待复习</b>：{{ output.toReview }}</p>
            <p><b>家庭建议</b>：{{ formatList(output.homeAdvice) }}</p>
            <p><b>下阶段目标</b>：{{ output.nextGoal }}</p>
          </div>
          <div v-else-if="taskType === 'ADVISOR_RENEWAL'">
            <p><b>内部提纲</b>：{{ output.internalOutline }}</p>
            <p><b>风险标记</b>：{{ formatList(output.riskFlags) }}</p>
          </div>
          <div v-else-if="taskType === 'RISK_EXPLAIN' || taskType === 'TCH_RISK_EXPLAIN'">
            <p class="ai-hint">分数由规则日批计算，模型不重算</p>
            <p><b>解释</b>：{{ output.explanation }}</p>
            <p><b>建议动作</b>：{{ actionLabel(output.suggestedAction) }}</p>
            <p><b>沟通要点</b>：{{ formatList(output.talkingPoints) }}</p>
          </div>
          <Collapse class="ai-raw-json">
            <Panel name="raw">
              原始 JSON
              <pre slot="content" class="ai-pre">{{ formatJson(output) }}</pre>
            </Panel>
          </Collapse>
        </div>
        <p v-else class="ai-muted">点击生成后展示结果</p>
      </Card>

      <!-- 块 4 确认 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">确认</p>
        <p class="ai-writeback-hint">{{ writebackHint }}</p>
        <Input type="textarea" :rows="5" v-model="editedText" :disabled="confirmReadOnly" placeholder="确认写入的文案"></Input>
        <div v-if="!isMarket" class="ai-confirm-btns">
          <Button type="primary" size="small" :loading="approving || executing" :disabled="!canConfirm" @click="onConfirmWriteback">确认并写回</Button>
          <Button size="small" :disabled="!canAbandon" @click="onAbandon">放弃</Button>
        </div>
      </Card>

      <!-- 块 5 反馈 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">反馈</p>
        <div class="ai-feedback-btns">
          <Button size="small" :disabled="!canFeedback" @click="onFeedback(1)">采用</Button>
          <Button size="small" :disabled="!canFeedback" @click="onFeedback(2)">修改后采用</Button>
          <Button size="small" :disabled="!canFeedback" @click="onFeedback(0)">不采用</Button>
        </div>
        <Select v-model="feedbackIssueType" clearable placeholder="问题类型（可选）" style="width: 180px; margin-top: 8px;">
          <Option value="FACT">事实有误</Option>
          <Option value="TONE">语气不当</Option>
          <Option value="POLICY">违反规范</Option>
          <Option value="OTHER">其他</Option>
        </Select>
        <Input type="textarea" :rows="2" v-model="feedbackComment" placeholder="补充说明（可选）" style="margin-top: 8px;"></Input>
      </Card>

      <!-- 块 6 历史 -->
      <Card class="ai-block" dis-hover>
        <p slot="title">历史</p>
        <p v-if="historyLoading" class="ai-muted">加载中…</p>
        <p v-else-if="!history.length" class="ai-muted">暂无历史</p>
        <div v-else class="ai-history">
          <p class="ai-hint">点击一条可把该次结果填回上方</p>
          <div
            v-for="item in history"
            :key="historyKey(item)"
            class="ai-history-item"
            :class="{'ai-history-item-active': historyKey(item) === runId}"
            @click="onHistoryClick(item)">
            <span class="ai-history-time">{{ historyTime(item) }}</span>
            <span>{{ historyTaskLabel(item) }}</span>
            <span>{{ historyStatusLabel(item) }}</span>
          </div>
        </div>
      </Card>
    </div>
    <div slot="footer">
      <Button @click="closeDrawer">关闭</Button>
    </div>
  </Modal>
</template>

<script>
import {
  AI_URI,
  newClientRequestId,
  buildPrepareBody,
  buildGenerateBody,
  stripForbiddenAiBody,
  isSensitiveFactKey
} from '@/tools/aiApi'

var ERROR_TEXT = {
  AI_SCOPE_DENIED: '无权查看该学生',
  AI_KILL_SWITCH: 'AI 已关闭',
  AI_BUDGET_EXCEEDED: '今日额度已用完',
  AI_RATE_LIMITED: '操作过于频繁',
  AI_LLM_TIMEOUT: '生成超时，请重试',
  AI_LLM_UNSAFE: '内容未通过安全校验',
  AI_SCHEMA_INVALID: '结果结构不合法，未写回',
  AI_STATE_INVALID: '状态不允许此操作',
  AI_MARKET_NO_WRITEBACK: '市场角色不能写回',
  AI_IDEMPOTENT_HIT: '已写回，未重复插入'
}

var TASK_LABEL = {
  SALES_FOLLOWUP: '跟进建议',
  SALES_CALL_ANALYSIS: '通话分析',
  ADVISOR_AFTER_CLASS: '课后反馈',
  ADVISOR_WEEKLY: '周报',
  ADVISOR_RENEWAL: '续费提纲',
  RISK_EXPLAIN: '风险解释',
  TCH_RISK_EXPLAIN: '老师风险解释'
}

var STATUS_LABEL = {
  DRAFT: '草稿',
  GENERATING: '生成中',
  GENERATED: '已生成',
  APPROVED: '已确认',
  EXECUTED: '已写回',
  FAILED: '失败',
  CANCELLED: '已放弃'
}

var WRITEBACK_HINT = {
  SALES_FOLLOWUP: '将写入表 user_add_remark。无人行则 INSERT，有人行则 旧remark + "。" + 本文。files 不改。update_id = 当前登录用户（服务端取，前端不传）。',
  SALES_CALL_ANALYSIS: '将写入表 user_add_remark，前缀 [AI通话分析]。无人行则 INSERT，有人行则 旧remark + "。" + 本文。',
  ADVISOR_AFTER_CLASS: '将写入 user_trace_record。trackType=61（上课跟进消息）。isMessageSend=0（不推送）。content 前缀 [AI课后]。',
  ADVISOR_WEEKLY: '将写入 user_trace_record。trackType=61（上课跟进消息）。isMessageSend=0（不推送）。content 前缀 [AI周报]。',
  ADVISOR_RENEWAL: '将写入 user_trace_record。trackType=61（上课跟进消息）。isMessageSend=0（不推送）。content 前缀 [AI续费提纲]。仅内部。',
  RISK_EXPLAIN: '将写入 student_risk_followup。可选同时写一条 user_trace_record，trackType=61，isMessageSend=0，前缀 [AI风险]。',
  TCH_RISK_EXPLAIN: '将写入 tch_track_record，类型回访，前缀 [AI老师风险]。可选同时记 teacher_risk_followup。'
}

export default {
  name: 'AiDrawer',
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    uid: {
      type: [Number, String],
      default: null
    },
    pageRoute: {
      type: String,
      required: true
    },
    defaultTaskType: {
      type: String,
      required: true
    },
    snapshotId: {
      type: [Number, String],
      default: null
    },
    subjectType: {
      type: String,
      default: 'STUDENT'
    }
  },
  data: function () {
    return {
      taskType: '',
      excludeFactKeys: [],
      facts: null,
      factSources: [],
      runId: null,
      runNo: null,
      runStatus: '',
      output: null,
      pending: null,
      editedText: '',
      originalEditedText: '',
      errorCode: '',
      errorMsg: '',
      clientRequestId: '',
      history: [],
      preparing: false,
      generating: false,
      approving: false,
      executing: false,
      historyLoading: false,
      prepareFailed: false,
      resultError: false,
      feedbackAdopted: null,
      feedbackIssueType: null,
      feedbackComment: '',
      suppressTaskChange: false
    }
  },
  computed: {
    isMarket: function () {
      return Number(this.$store.get('adminRule')) === 4
    },
    missingIdText: function () {
      return this.subjectType === 'TEACHER' ? '缺少老师 ID' : '缺少学生 ID'
    },
    taskOptions: function () {
      var route = this.pageRoute
      if (route === '/crm/PhoneRecordList') {
        return [
          {label: '跟进建议', value: 'SALES_FOLLOWUP'},
          {label: '通话分析', value: 'SALES_CALL_ANALYSIS'}
        ]
      }
      if (route === '/user/bookTestList' || route === '/user/userExperienceList') {
        return [{label: '跟进建议', value: 'SALES_FOLLOWUP'}]
      }
      if (route === '/user/userPayList' || route === '/user/userTodayNewList' || route === '/user/userTrackList') {
        return [
          {label: '课后反馈', value: 'ADVISOR_AFTER_CLASS'},
          {label: '周报', value: 'ADVISOR_WEEKLY'},
          {label: '续费提纲', value: 'ADVISOR_RENEWAL'}
        ]
      }
      if (route === '/user/studentRiskList') {
        return [{label: '风险解释', value: 'RISK_EXPLAIN'}]
      }
      if (route === '/tch/tchFormalList' || route === '/tch/filipino/tchFormalList') {
        return [{label: '风险解释', value: 'TCH_RISK_EXPLAIN'}]
      }
      return []
    },
    writebackHint: function () {
      return WRITEBACK_HINT[this.taskType] || ''
    },
    displayError: function () {
      if (this.errorCode && ERROR_TEXT[this.errorCode]) {
        return ERROR_TEXT[this.errorCode] + (this.errorCode ? '（' + this.errorCode + '）' : '')
      }
      if (this.errorMsg) {
        return this.errorMsg + (this.errorCode ? '（' + this.errorCode + '）' : '')
      }
      if (this.errorCode) {
        return this.errorCode
      }
      return ''
    },
    factRows: function () {
      var self = this
      var facts = this.facts
      if (!facts || typeof facts !== 'object' || Array.isArray(facts)) {
        return []
      }
      var sourceMap = {}
      ;(this.factSources || []).forEach(function (item) {
        if (item && item.key) {
          sourceMap[item.key] = item.table
        }
      })
      return Object.keys(facts).filter(function (key) {
        if (isSensitiveFactKey(key)) {
          console.warn('[AiDrawer] skip sensitive fact key', key)
          return false
        }
        return true
      }).map(function (key) {
        return {
          key: key,
          display: self.formatValue(facts[key]),
          table: sourceMap[key] || ''
        }
      })
    },
    canGenerate: function () {
      if (!this.uid) {
        return false
      }
      if (this.generating || this.approving || this.executing) {
        return false
      }
      if (this.runStatus === 'GENERATING' || this.runStatus === 'APPROVED') {
        return false
      }
      return true
    },
    canConfirm: function () {
      if (this.isMarket) {
        return false
      }
      if (this.runStatus !== 'GENERATED') {
        return false
      }
      if (!this.editedText || !String(this.editedText).trim()) {
        return false
      }
      if (this.executing || this.approving) {
        return false
      }
      if (!this.uid) {
        return false
      }
      return true
    },
    canAbandon: function () {
      return this.runStatus === 'GENERATED' && !this.isMarket
    },
    canFeedback: function () {
      if (!this.runId) {
        return false
      }
      if (!this.runStatus || this.runStatus === 'GENERATING' || this.runStatus === 'DRAFT') {
        return false
      }
      return true
    },
    confirmReadOnly: function () {
      return this.runStatus === 'EXECUTED' || this.runStatus === 'CANCELLED'
    }
  },
  watch: {
    visible: function (val) {
      if (val) {
        this.resetAndOpen()
      }
    }
  },
  methods: {
    dealLabel: function (code) {
      if (code === 'HIGH') return '高'
      if (code === 'MID') return '中'
      if (code === 'LOW') return '低'
      return code || ''
    },
    closeDrawer: function () {
      this.$emit('update:visible', false)
      this.$emit('on-close', false)
    },
    onVisibleChange: function (val) {
      if (!val) {
        this.$emit('update:visible', false)
        this.$emit('on-close', false)
      }
    },
    resetState: function () {
      this.excludeFactKeys = []
      this.facts = null
      this.factSources = []
      this.runId = null
      this.runNo = null
      this.runStatus = ''
      this.output = null
      this.pending = null
      this.editedText = ''
      this.originalEditedText = ''
      this.errorCode = ''
      this.errorMsg = ''
      this.clientRequestId = ''
      this.history = []
      this.preparing = false
      this.generating = false
      this.approving = false
      this.executing = false
      this.historyLoading = false
      this.prepareFailed = false
      this.resultError = false
      this.feedbackAdopted = null
      this.feedbackIssueType = null
      this.feedbackComment = ''
    },
    resetAndOpen: function () {
      var self = this
      this.suppressTaskChange = true
      this.resetState()
      this.taskType = this.defaultTaskType || ''
      this.$nextTick(function () {
        self.suppressTaskChange = false
        if (!self.uid) {
          self.errorMsg = self.missingIdText
          self.resultError = true
          return
        }
        self.doPrepare()
        self.doLoadHistory()
      })
    },
    onTaskTypeChange: function () {
      if (this.suppressTaskChange) {
        return
      }
      this.output = null
      this.pending = null
      this.runId = null
      this.runNo = null
      this.runStatus = ''
      this.errorCode = ''
      this.errorMsg = ''
      this.editedText = ''
      this.originalEditedText = ''
      this.clientRequestId = ''
      this.resultError = false
      this.prepareFailed = false
      this.doPrepare()
    },
    buildBaseBody: function () {
      var body = buildPrepareBody({
        taskType: this.taskType,
        uid: this.uid,
        pageRoute: this.pageRoute,
        excludeFactKeys: this.excludeFactKeys,
        subjectType: this.subjectType
      })
      if (this.snapshotId) {
        body.snapshotId = this.snapshotId
      }
      return stripForbiddenAiBody(body)
    },
    pickErrorCode: function (res) {
      if (!res) {
        return ''
      }
      var data = res.data || {}
      return data.errorCode || data.error_code || ''
    },
    applyError: function (res, where) {
      var code = this.pickErrorCode(res)
      var msg = (res && (res.msg || (res.data && res.data.msg))) || ''
      this.errorCode = code
      this.errorMsg = msg
      if (where === 'prepare') {
        this.prepareFailed = true
      }
      if (where === 'generate') {
        this.resultError = true
        this.runStatus = 'FAILED'
        if (res && res.data && res.data.runId) {
          this.runId = res.data.runId
        }
      }
      if (where === 'result') {
        this.resultError = true
      }
    },
    isIdempotentSuccess: function (res) {
      return this.pickErrorCode(res) === 'AI_IDEMPOTENT_HIT'
    },
    isFactIncluded: function (key) {
      return this.excludeFactKeys.indexOf(key) === -1
    },
    onFactCheck: function (key, checked) {
      this.toggleFact(key, checked)
    },
    toggleFact: function (key, checked) {
      var idx = this.excludeFactKeys.indexOf(key)
      if (checked) {
        if (idx !== -1) {
          this.excludeFactKeys.splice(idx, 1)
        }
      } else if (idx === -1) {
        this.excludeFactKeys.push(key)
      }
    },
    doPrepare: function () {
      var self = this
      if (!self.uid) {
        self.errorMsg = self.missingIdText
        self.prepareFailed = true
        return
      }
      self.preparing = true
      self.prepareFailed = false
      self.errorCode = ''
      self.errorMsg = ''
      self.$http.post(AI_URI.prepare, self.buildBaseBody()).then(function (res) {
        self.preparing = false
        if (!res || res.status !== 'ok') {
          self.applyError(res, 'prepare')
          return
        }
        var data = res.data || {}
        self.facts = data.facts || null
        self.factSources = data.factSources || []
        if (data.excludedKeys && data.excludedKeys.length) {
          self.excludeFactKeys = data.excludedKeys.slice()
        }
      }).catch(function () {
        self.preparing = false
        self.prepareFailed = true
        self.errorMsg = '系统繁忙'
      })
    },
    onGenerate: function () {
      var self = this
      if (!self.canGenerate) {
        return
      }
      if (!self.clientRequestId || self.runStatus !== 'GENERATING') {
        self.clientRequestId = newClientRequestId()
      }
      self.generating = true
      self.runStatus = 'GENERATING'
      self.resultError = false
      self.errorCode = ''
      self.errorMsg = ''
      var body = stripForbiddenAiBody(buildGenerateBody(self.buildBaseBody(), self.clientRequestId))
      self.$http.post(AI_URI.generate, body).then(function (res) {
        self.generating = false
        if (!res || res.status !== 'ok') {
          self.applyError(res, 'generate')
          return
        }
        var data = res.data || {}
        self.runId = data.runId
        self.runNo = data.runNo
        self.runStatus = data.status || 'GENERATED'
        self.output = data.output || null
        self.pending = data.pending || null
        self.editedText = self.defaultEditedText(self.output)
        self.originalEditedText = self.editedText
        if (self.runStatus === 'FAILED') {
          self.applyError(res, 'generate')
        }
      }).catch(function () {
        self.generating = false
        self.runStatus = 'FAILED'
        self.resultError = true
        self.errorMsg = '系统繁忙'
      })
    },
    defaultEditedText: function (output) {
      if (!output) {
        return ''
      }
      var t = this.taskType
      if (t === 'SALES_FOLLOWUP') {
        return output.followUpText || ''
      }
      if (t === 'SALES_CALL_ANALYSIS') {
        return this.withPrefix('[AI通话分析]', output.followUpText)
      }
      if (t === 'ADVISOR_AFTER_CLASS') {
        return this.withPrefix('[AI课后]', output.parentFeedback)
      }
      if (t === 'ADVISOR_WEEKLY') {
        var parts = []
        if (output.overview) {
          parts.push(output.overview)
        }
        if (output.highlights) {
          parts.push(this.formatList(output.highlights))
        }
        if (output.toReview) {
          parts.push(output.toReview)
        }
        if (output.homeAdvice) {
          parts.push(this.formatList(output.homeAdvice))
        }
        if (output.nextGoal) {
          parts.push(output.nextGoal)
        }
        return this.withPrefix('[AI周报]', parts.join('\n'))
      }
      if (t === 'ADVISOR_RENEWAL') {
        return this.withPrefix('[AI续费提纲]', output.internalOutline)
      }
      if (t === 'RISK_EXPLAIN') {
        return output.explanation || ''
      }
      if (t === 'TCH_RISK_EXPLAIN') {
        return output.explanation || ''
      }
      return ''
    },
    actionLabel: function (code) {
      var map = {
        CALL: '电话',
        REMARK: '备注',
        TRANSFER: '转交',
        IGNORE: '忽略'
      }
      return map[code] || code || ''
    },
    withPrefix: function (prefix, text) {
      var body = text || ''
      if (typeof body !== 'string') {
        body = this.formatValue(body)
      }
      if (!body) {
        return prefix
      }
      if (body.indexOf(prefix) === 0) {
        return body
      }
      return prefix + body
    },
    onConfirmWriteback: function () {
      var self = this
      if (!self.canConfirm) {
        return
      }
      self.approving = true
      self.executing = true
      self.errorCode = ''
      self.errorMsg = ''
      self.$http.post(AI_URI.approve, {
        runId: self.runId,
        editedText: self.editedText
      }).then(function (res) {
        if (self.isIdempotentSuccess(res)) {
          self.markExecuted(true)
          return
        }
        if (!res || res.status !== 'ok') {
          self.applyError(res, 'result')
          return
        }
        self.runStatus = 'APPROVED'
        return self.$http.post(AI_URI.execute, {runId: self.runId}).then(function (execRes) {
          if (self.isIdempotentSuccess(execRes) || (execRes && execRes.status === 'ok')) {
            self.markExecuted(self.isIdempotentSuccess(execRes))
          } else {
            self.applyError(execRes, 'result')
          }
        })
      }).catch(function () {
        self.errorMsg = '系统繁忙'
        self.resultError = true
      }).then(function () {
        self.approving = false
        self.executing = false
      })
    },
    markExecuted: function (idempotent) {
      this.runStatus = 'EXECUTED'
      this.resultError = false
      if (idempotent) {
        this.errorCode = 'AI_IDEMPOTENT_HIT'
        this.errorMsg = ERROR_TEXT.AI_IDEMPOTENT_HIT
      } else {
        this.errorCode = ''
        this.errorMsg = ''
      }
      this.$emit('on-executed', {
        runId: this.runId,
        taskType: this.taskType,
        uid: this.uid
      })
    },
    onAbandon: function () {
      this.runStatus = 'CANCELLED'
    },
    onFeedback: function (adopted) {
      var self = this
      if (!self.canFeedback) {
        return
      }
      var edited = self.editedText !== self.originalEditedText
      self.$http.post(AI_URI.feedback, {
        runId: self.runId,
        adopted: adopted ? 1 : 0,
        edited: edited ? 1 : 0,
        issueType: self.feedbackIssueType,
        comment: self.feedbackComment
      }).then(function (res) {
        if (res && res.status === 'ok') {
          self.feedbackAdopted = adopted
          self.$Message.success('反馈已提交')
        }
      }).catch(function () {})
    },
    doLoadHistory: function () {
      var self = this
      if (!self.uid) {
        return
      }
      self.historyLoading = true
      self.$http.post(AI_URI.runList, {
        subjectType: self.subjectType || 'STUDENT',
        subjectId: self.uid,
        pageNo: 1,
        pageSize: 20
      }).then(function (res) {
        self.historyLoading = false
        if (!res || res.status !== 'ok') {
          return
        }
        self.history = self.normalizeList(res.data)
      }).catch(function () {
        self.historyLoading = false
      })
    },
    normalizeList: function (data) {
      if (!data) {
        return []
      }
      if (Array.isArray(data)) {
        return data
      }
      if (Array.isArray(data.data)) {
        return data.data
      }
      if (Array.isArray(data.list)) {
        return data.list
      }
      return []
    },
    historyKey: function (item) {
      return item.runId || item.id || item.runNo
    },
    historyTaskLabel: function (item) {
      var code = (item && (item.taskType || item.task_type)) || ''
      return TASK_LABEL[code] || code || '未知任务'
    },
    historyStatusLabel: function (item) {
      var code = (item && item.status) || ''
      return STATUS_LABEL[code] || code || ''
    },
    historyTime: function (item) {
      var raw = item && (item.gmtCreate || item.gmt_create || item.createTime || item.createdAt)
      return this.formatHistoryTime(raw)
    },
    formatHistoryTime: function (raw) {
      if (raw == null || raw === '') {
        return ''
      }
      var d
      if (typeof raw === 'number') {
        d = new Date(raw < 1e12 ? raw * 1000 : raw)
      } else if (raw instanceof Date) {
        d = raw
      } else {
        var s = String(raw).replace('T', ' ').replace(/-/g, '/')
        d = new Date(s)
        if (isNaN(d.getTime())) {
          return String(raw)
        }
      }
      if (isNaN(d.getTime())) {
        return String(raw)
      }
      function pad (n) {
        return n < 10 ? '0' + n : '' + n
      }
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
        ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    parseJsonMaybe: function (raw) {
      if (raw == null || raw === '') {
        return null
      }
      if (typeof raw === 'object') {
        return raw
      }
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    pickField: function (obj, names) {
      if (!obj) {
        return undefined
      }
      for (var i = 0; i < names.length; i++) {
        if (obj[names[i]] !== undefined && obj[names[i]] !== null) {
          return obj[names[i]]
        }
      }
      return undefined
    },
    onHistoryClick: function (item) {
      var self = this
      var runId = item.runId || item.id
      if (!runId) {
        return
      }
      self.$http.post(AI_URI.runDetail, {runId: runId}).then(function (res) {
        if (!res || res.status !== 'ok') {
          self.applyError(res, 'result')
          return
        }
        var data = res.data || {}
        var run = data.run || data
        var payload = data.payload || {}
        var pending = data.pending || null
        var taskType = self.pickField(run, ['taskType', 'task_type']) || item.taskType || item.task_type
        var output = self.parseJsonMaybe(
          self.pickField(payload, ['outputJson', 'output_json'])
        ) || data.output || null
        var editedObj = self.parseJsonMaybe(
          self.pickField(payload, ['editedJson', 'edited_json'])
        )
        var facts = self.parseJsonMaybe(
          self.pickField(payload, ['inputRedacted', 'input_redacted'])
        ) || data.facts
        if (taskType) {
          self.suppressTaskChange = true
          self.taskType = taskType
          self.$nextTick(function () {
            self.suppressTaskChange = false
          })
        }
        self.runId = self.pickField(run, ['id', 'runId']) || runId
        self.runNo = self.pickField(run, ['runNo', 'run_no'])
        self.runStatus = self.pickField(run, ['status']) || item.status || ''
        self.output = output
        self.pending = pending
        if (facts && typeof facts === 'object' && !Array.isArray(facts)) {
          self.facts = facts
        }
        var edited = ''
        if (data.editedText) {
          edited = data.editedText
        } else if (pending && pending.previewText) {
          edited = pending.previewText
        } else if (editedObj && typeof editedObj === 'object') {
          edited = self.defaultEditedText(editedObj)
        } else if (typeof editedObj === 'string') {
          edited = editedObj
        } else {
          edited = self.defaultEditedText(self.output)
        }
        self.editedText = edited
        self.originalEditedText = edited
        self.resultError = self.runStatus === 'FAILED'
        self.errorCode = ''
        self.errorMsg = ''
        if (self.runStatus === 'FAILED') {
          self.applyError(res, 'result')
          return
        }
        if (!self.output) {
          self.resultError = true
          self.errorMsg = '该次没有可展示的结果'
          return
        }
        self.$Message.success('已加载该次结果')
      }).catch(function () {
        self.resultError = true
        self.errorMsg = '系统繁忙'
      })
    },
    formatList: function (val) {
      if (!val) {
        return ''
      }
      if (Array.isArray(val)) {
        return val.join('；')
      }
      return this.formatValue(val)
    },
    formatValue: function (val) {
      if (val === null || val === undefined || val === '') {
        return ''
      }
      if (typeof val === 'object') {
        try {
          return JSON.stringify(val)
        } catch (e) {
          return String(val)
        }
      }
      return String(val)
    },
    formatJson: function (val) {
      try {
        return JSON.stringify(val, null, 2)
      } catch (e) {
        return String(val)
      }
    }
  }
}
</script>

<style>
.ai-drawer-modal.ivu-modal-wrap {
  overflow: hidden;
}
.ai-drawer-modal .ivu-modal,
.ivu-modal.ai-drawer-modal {
  top: 0;
  right: 0;
  left: auto;
  height: 100%;
  margin: 0 0 0 auto;
}
.ai-drawer-modal .ivu-modal-content,
.ivu-modal.ai-drawer-modal .ivu-modal-content {
  height: 100vh;
  border-radius: 0;
  display: flex;
  flex-direction: column;
}
.ai-drawer-modal .ivu-modal-body,
.ivu-modal.ai-drawer-modal .ivu-modal-body {
  flex: 1;
  overflow: auto;
}
</style>

<style scoped>
.ai-drawer-body {
  padding-bottom: 8px;
}
.ai-block {
  margin-bottom: 12px;
}
.ai-hint {
  color: #80848f;
  font-size: 12px;
  margin-top: 8px;
}
.ai-muted {
  color: #c5c8ce;
}
.ai-error {
  color: #ed4014;
  white-space: pre-wrap;
}
.ai-fact-row {
  margin-bottom: 6px;
  line-height: 1.5;
}
.ai-fact-key {
  font-weight: 600;
  margin-right: 6px;
}
.ai-fact-value {
  display: inline-block;
  margin-right: 8px;
  word-break: break-all;
}
.ai-fact-source {
  color: #80848f;
  font-size: 12px;
}
.ai-result-toolbar {
  margin-bottom: 8px;
}
.ai-result p {
  margin-bottom: 6px;
  word-break: break-word;
}
.ai-writeback-hint {
  color: #515a6e;
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 8px;
  white-space: pre-wrap;
}
.ai-confirm-btns,
.ai-feedback-btns {
  margin-top: 8px;
}
.ai-history-item {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid #e8eaec;
  cursor: pointer;
  font-size: 12px;
  color: #2d8cf0;
}
.ai-history-item-active {
  font-weight: 600;
}
.ai-history-time {
  color: #515a6e;
}
.ai-pre {
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
}
</style>



// WEBPACK FOOTER //
// AiDrawer.vue?96a7c0da